/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { useEffect, useState } from "react";

import ToolbarButton from "../ToolbarButton";
import SetPositionButton from "../SetPositionButton";

import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatColorFillIcon from "@material-ui/icons/FormatColorFill";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";

import DublicateIcon from "@material-ui/icons/FileCopy";

import { SketchPicker } from "react-color";

import Slider from "@material-ui/core/Slider";

import { throttle } from "underscore";

import { useStoreon } from "storeon/react";

import { SIZES } from "../../constants";

import t from "translations";

const FamilySelect = styled.div`
  border-radius: 4px;
  min-width: 100px;
  padding: 5px;
  padding-left: 14px;
  justify-content: space-between;
  display: flex;
  cursor: pointer;
  align-items: center;
  transition: all 0.2s ease-in-out;
  margin-right: 12px;
  color: #444;
  background: ${props => (props.active ? "#ccc" : "none")};
  :hover {
    background: #ddd;
  }
`;

const ButtonsGroupRow = styled(ToggleButtonGroup)`
  flex-wrap: nowrap;
  flex-direction: row;
  display: flex;
`;

const TextButtons = props => {
  const { activeObject } = props;
  const { dispatch, activeMenu, canvas } = useStoreon("activeMenu", "canvas");

  const [aligment, setAligment] = useState(activeObject.textAlign);

  const [color, setColor] = useState(activeObject.fill);
  const [fontSize, setFontSize] = useState(activeObject.fontSize);

  useEffect(() => {
    const objFormats = [];
    if (activeObject.fontWeight === 700 || activeObject.fontWeight === 600) {
      objFormats.push("bold");
    }
    if (activeObject.fontStyle === "italic") {
      objFormats.push("italic");
    }
    setFormats(objFormats);
    setAligment(activeObject.textAlign);
    setFontSize(activeObject.scaleX);
    setColor(activeObject.fill);
  }, [activeObject]);

  const [formats, setFormats] = useState([]);

  const handleFormat = (event, newFormats) => {
    if (!newFormats) return;
    const ao = canvas.getActiveObject();
    if (newFormats.includes("bold")) {
      ao.fontWeight = 700;
    } else {
      ao.fontWeight = 400;
    }
    if (newFormats.includes("italic")) {
      ao.fontStyle = "italic";
    } else {
      ao.fontStyle = "normal";
    }
    setFormats(newFormats);
    canvas.requestRenderAll();
  };
  const handleAlignment = (event, newAlignment) => {
    if (!newAlignment) {
      return;
    }
    const obj = canvas.getActiveObject();
    if (newAlignment === "left") {
      obj.set("originX", "left");
    }
    if (newAlignment === "right") {
      obj.set("originX", "right");
    }
    if (newAlignment === "center") {
      obj.set("originX", "center");
    }
    obj.set("originY", "top");
    obj.setCoords();
    obj.textAlign = newAlignment;
    setAligment(newAlignment);
    canvas.fire("text:format", {
      target: canvas.getActiveObject()
    });
    canvas.requestRenderAll();
  };

  const triggerColorChange = throttle(e => {
    canvas.fire("text:color", {
      target: canvas.getActiveObject()
    });
  }, 500);

  let isBoldDisabled = false;
  let isItalicDisabled = false;
  if (activeObject.fontData) {
    const { fontData } = activeObject;
    if (!fontData.files["700"]) {
      isBoldDisabled = true;
    }
    if (!fontData.files["italic"]) {
      isItalicDisabled = true;
    }
  }

  return (
    <React.Fragment>
      <div>
        <div
          css={css`
            width: 200px;
            margin-right: 20px;
            align-items: center;
            justify-content: center;
            display: flex;
          `}
        >
          <Slider
            onChange={(e, val) => {
              let ao;
              if (!canvas || !(ao = canvas.getActiveObject())) {
                return;
              }
              ao.set("scaleX", val).setCoords();
              ao.set("scaleY", val).setCoords();
              setFontSize(val);
              canvas.requestRenderAll();
            }}
            min={0.2}
            max={3}
            step={0.05}
            value={fontSize}
          />{" "}
          <Typography style={{ marginLeft: 20 }}>
            {(fontSize * 100).toFixed(0)}
          </Typography>
        </div>
        <FamilySelect
          active={activeMenu === 99}
          onClick={_ => {
            if (activeMenu === 99) {
              dispatch("menu/setActiveMenu", 0);
            } else {
              dispatch("menu/setActiveMenu", 99);
            }
          }}
        >
          <div>{activeObject.fontFamily}</div>
          <ArrowDropDownIcon />
        </FamilySelect>

        <ButtonsGroupRow
          onChange={handleAlignment}
          value={aligment}
          exclusive
          css={css`
            display: none;
            margin-right: 20px;
            @media screen and (max-width: 480px) {
              display: none;
            }
          `}
        >
          <ToggleButton value="left">
            <FormatAlignLeftIcon />
          </ToggleButton>
          <ToggleButton value="center">
            <FormatAlignCenterIcon />
          </ToggleButton>
          <ToggleButton value="right">
            <FormatAlignRightIcon />
          </ToggleButton>
        </ButtonsGroupRow>
        <div
          css={css`
            svg {
              color: #565656;
            }
          `}
        >
          <ButtonsGroupRow value={formats} onChange={handleFormat}>
            <ToggleButton value="bold" disabled={isBoldDisabled}>
              <FormatBoldIcon />
            </ToggleButton>
            <ToggleButton value="italic" disabled={isItalicDisabled}>
              <FormatItalicIcon />
            </ToggleButton>
            <ToggleButton value="color">
              <FormatColorFillIcon />
              <ArrowDropDownIcon />
            </ToggleButton>
          </ButtonsGroupRow>
          {formats.includes("color") ? (
            <SketchPicker
              disableAlpha
              color={color}
              css={css`
                position: fixed;
                top: ${48 + SIZES.topBar}px;
            @media screen and (max-width: 480px) {
              left: 15px;
            }
              `}
              onChange={(color, event) => {
                setColor(color.hex);
                canvas.getActiveObject().set({fill: color.hex});
                canvas.requestRenderAll();
              }}
              onChangeComplete={(color, event) => {
                setColor(color.hex);
                triggerColorChange();
                canvas.fire("object:modified", {
                  target: canvas.getActiveObject()
                });
              }}
            />
          ) : (
            ""
          )}
        </div>
      </div>
      <div>
        <SetPositionButton
          canvas={canvas}
        />
        <ToolbarButton
          desc={t.dublicate}
          tooltip
          icon={<DublicateIcon />}
          onClick={() => {
            canvas.getActiveObject().clone(cloned => {
              cloned.top += cloned.height * 0.2 * cloned.scaleY;
              cloned.left += cloned.width * 0.2 * cloned.scaleX;
              cloned.generateId();
              canvas.add(cloned);
              cloned.bringToFront();
              canvas.discardActiveObject();
              canvas.setActiveObject(cloned);
            });
          }}
        />
        <ToolbarButton
          desc={t.remove}
          tooltip
          icon={<DeleteIcon />}
          onClick={() => {
            canvas.remove(canvas.getActiveObject());
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default TextButtons;
